import { Form, Formik } from "formik";
import React, { useState, useEffect } from "react";
import * as Yup from "yup";
import AddButton from "../../buttons/button";
import ApproveDeclineActions from "../approve-decline-actions";
import InputField from "../../forms/input-field";
import ImageField from "../../forms/images-field";
import CheckBox from "../../forms/check-box-field";
import { useDispatch } from "react-redux";
import { StoreAgreementCreateAction } from "../../../redux/actions/store-action";
import { api } from "../../../redux/apis/api";
import successToast from "../../helper-components/success-toast";
import errorToast from "../../helper-components/error-toast";
import PDFViewer from "../../helper-components/pdf-viewer";

const StoreAgreementTab = ({
  id,
  setStoreData,
  storeData,
  initialValue,
  disabled = false,
  permissionFn = () => {},
}) => {
  const dispatch = useDispatch();
  const [agreementPdfUrl, setAgreementPdfUrl] = useState(
    initialValue?.store_agreement_document ?? initialValue?.agreement_pdf_url ?? ""
  );

  useEffect(() => {
    if (!agreementPdfUrl) {
      const personal = storeData?.personal ?? {};
      const firm = storeData?.firm ?? {};
      const address = storeData?.address ?? {};
      const loi = storeData?.loi ?? {};

      const today = new Date().toISOString().split("T")[0];

      const payload = {
        store_id: id,
        franchisee_name: personal?.authorised_person_name ?? "",
        age: personal?.age ?? "",
        father_or_spouse: firm?.partners?.[0]?.partner_name ?? "",
        residential_address: personal?.residential_address ?? "",
        business_name: firm?.firm_name ?? "",
        pan: firm?.pan_number ?? "",
        phone: personal?.phone_number ?? "",
        email: personal?.email ?? "",
        store_location: address?.shop_address ?? "",
        store_code: address?.store_code ?? "",
        witness: initialValue?.agreement_witness ?? "",
        date: today,
        signature: loi?.digital_sign ?? "",
      };

      api
        .post("/cpos/app/getAgreementPdf", payload)
        .then((res) => {
          const url = res.data?.data?.pdf_url ?? res.data?.data?.url ?? "";
          if (url) setAgreementPdfUrl(url);
          else errorToast("Failed to load agreement document");
        })
        .catch(() => errorToast("Failed to load agreement document"));
    }
  }, []);

  const initialValues = {
    agreement_witness: initialValue?.agreement_witness ?? "",
    store_agreement_remarks: initialValue?.store_agreement_remarks ?? "",
    store_agreement_digital_sign: initialValue?.store_agreement_digital_sign ?? "",
    store_agreement_with_davaindia_conditions:
      initialValue?.store_agreement_with_davaindia_conditions ?? false,
    store_agreement_is_approve: initialValue?.store_agreement_is_approve ?? false,
    store_agreement_is_reject: initialValue?.store_agreement_is_reject ?? false,
  };

  const validationSchema = Yup.object().shape({});
  const onSubmit = (values) => {
    const payload = {
      store_id: id ?? null,
      agreement_witness: values.agreement_witness,
      store_agreement_remarks: values.store_agreement_remarks,
      store_agreement_digital_sign: values.store_agreement_digital_sign,
      store_agreement_document: agreementPdfUrl,
      store_agreement_with_davaindia_conditions:
        values.store_agreement_with_davaindia_conditions,
    };
    dispatch(StoreAgreementCreateAction(payload, onSuccess, onError));
  };

  const onSuccess = (data) => {
    setStoreData({
      ...storeData,
      agreement: data?.data,
    });
    successToast(data?.data?.message);
  };
  const onError = (data) => {
    errorToast(data?.data?.message);
  };

  return (
    <Formik
      enableReinitialize={true}
      initialValues={initialValues}
      // validationSchema={validationSchema}
      onSubmit={onSubmit}
    >
      {({ values, setFieldValue, initialValues: savedValues }) => (
        <Form style={{ height: "calc(100% - 5rem)" }} className="pt-5">
          <div className="flex flex-col justify-between h-full">
            <div>
              <div className="w-full 2xl:w-[40%] xl:w-[50%] lg:w-[60%] px-5 xl:px-10">
                <InputField
                  isDisabled={disabled}
                  type="text"
                  name="agreement_witness"
                  placeholder="Enter Agreement Witness"
                  label="Agreement Witness"
                  labelWidth="10.5rem"
                />
                <InputField
                  isDisabled={disabled}
                  type="text"
                  name="store_agreement_remarks"
                  placeholder="Enter Remarks"
                  label="Remarks"
                  labelWidth="10.5rem"
                />
                <ImageField
                  isDisabled={disabled}
                  label="Digital Signature"
                  labelWidth="10.5rem"
                  name="store_agreement_digital_sign"
                />
              </div>
              {agreementPdfUrl && (
                <div className="px-5 xl:px-10 mt-4">
                  <p className="text-sm font-medium text-gray-600 mb-2">
                    Agreement Document
                  </p>
                  <div style={{ width: "100%", height: "800px" }}>
                    <embed
                      src={agreementPdfUrl}
                      type="application/pdf"
                      width="100%"
                      height="100%"
                    />
                  </div>
                </div>
              )}
            </div>
            <div className="sticky" style={{ bottom: "0" }}>
              <div
                style={{ height: "5rem" }}
                className="px-5 xl:px-10 bg-D0D5DD flex items-center"
              >
                <CheckBox items={agreementTerms} />
              </div>
              <div className="flex items-center justify-between px-10 mb-5x bg-white py-4 invoice-bottom-container">
                {permissionFn("store_document_process")?.approver ? (
                  <ApproveDeclineActions
                    storeId={id}
                    approveKey="store_agreement_is_approve"
                    rejectKey="store_agreement_is_reject"
                    isApproved={!!values?.store_agreement_is_approve}
                    isRejected={!!values?.store_agreement_is_reject}
                    disabled={disabled}
                    formValues={values}
                    savedValues={savedValues}
                    onDecided={async (data) => {
                      await setFieldValue(
                        "store_agreement_is_approve",
                        !!data?.store_agreement_is_approve
                      );
                      await setFieldValue(
                        "store_agreement_is_reject",
                        !!data?.store_agreement_is_reject
                      );
                    }}
                  />
                ) : (
                  <span />
                )}
                {!disabled && (
                  <AddButton prefixIcon={false} text="Save" type="submit" />
                )}
              </div>
            </div>
          </div>
        </Form>
      )}
    </Formik>
  );
};

export default StoreAgreementTab;

const agreementTerms = [
  {
    label: "I agree to the store agreement and the terms and conditions contained therein.",
    name: "store_agreement_with_davaindia_conditions",
    isRequired: false,
  },
];